import PocketBase, { type AuthRecord, type RecordService } from "pocketbase";

import { isAuthError } from "./errors";
import {
  Collections,
  type CollectionResponses,
  type TypedPocketBase,
  type UsersResponse,
} from "./pb-types";

/**
 * The single PocketBase client for the whole app.
 *
 * Every surface (Tauri desktop, mobile PWA, desktop browser) ships this same
 * module and talks to the same REST + realtime API, so there is exactly one
 * place that knows the server URL and holds the session.
 *
 * The client is typed with `TypedPocketBase` from the generated schema, so
 * `pb.collection("tasks")` yields fully typed records and a typo in a collection
 * name is a compile error rather than a runtime 404. Regenerate the types with
 * `npm run pb:types` after any schema change.
 */

/** Names of every collection in the schema. */
export type CollectionName = keyof CollectionResponses;

export const pb = new PocketBase(pbBaseUrl()) as TypedPocketBase;

/**
 * TanStack Query owns request lifecycles (deduplication, cancellation, retry),
 * so PocketBase's own auto-cancellation would only fight it: two components
 * rendering the same query would cancel each other mid-flight.
 */
pb.autoCancellation(false);

/**
 * Base URL of the server.
 *
 * `VITE_PB_URL` wins when set. Otherwise we fall back to the local instance
 * that `npm run pb:dev` starts, which is what a developer wants by default.
 */
function pbBaseUrl(): string {
  return import.meta.env.VITE_PB_URL?.trim() || "http://127.0.0.1:8090";
}

export const PB_URL: string = pbBaseUrl();

/** The origin shown to the user when the connection needs explaining. */
export const PB_DISPLAY_URL: string = (() => {
  try {
    return new URL(PB_URL, window.location.origin).host;
  } catch {
    return PB_URL;
  }
})();

/** The auth collection, typed once so features do not repeat the string. */
export function usersCollection(): RecordService<UsersResponse> {
  return pb.collection(Collections.Users);
}

/**
 * Narrow the auth store's generic record to the app's user type.
 *
 * `pb.authStore.record` is typed as a bare `RecordModel` because the store is
 * collection-agnostic. Kobo only ever authenticates against `users`, so this
 * is the one place that assertion is made, rather than at every call site.
 */
export function asUser(record: AuthRecord): UsersResponse | null {
  return (record as UsersResponse | null) ?? null;
}

/** The user currently held in the auth store, if any. */
export function currentUser(): UsersResponse | null {
  return asUser(pb.authStore.record);
}

/**
 * Files are protected, so a URL needs the signed-in user's file token.
 *
 * The URL has to come out of a synchronous render, so the token is fetched ahead
 * of time and held here. It lasts 30 days on the server and is kept across launches:
 * a new token would change every image URL and throw away the browser's cache.
 */
const FILE_TOKEN_KEY = "kobo.fileToken";
const FILE_TOKEN_RENEW_MS = 7 * 24 * 60 * 60_000;

type StoredFileToken = { user: string; token: string; at: number };

function readStoredFileToken(): StoredFileToken | null {
  try {
    return JSON.parse(window.localStorage.getItem(FILE_TOKEN_KEY) ?? "null") as StoredFileToken | null;
  } catch {
    return null;
  }
}

let storedFileToken = readStoredFileToken();

function heldFileToken(): string {
  const userId = pb.authStore.record?.id;
  return userId && storedFileToken?.user === userId ? storedFileToken.token : "";
}

/** Fetch a file token if the user has none or it is getting old. Offline keeps the old one. */
export async function renewFileToken(): Promise<void> {
  const userId = pb.authStore.record?.id;
  if (!userId || !pb.authStore.isValid) return;
  if (heldFileToken() && Date.now() - (storedFileToken?.at ?? 0) < FILE_TOKEN_RENEW_MS) return;
  try {
    storedFileToken = { user: userId, token: await pb.files.getToken({ requestKey: null }), at: Date.now() };
    window.localStorage.setItem(FILE_TOKEN_KEY, JSON.stringify(storedFileToken));
  } catch (error) {
    console.warn("[Kobo] could not renew the file token", error);
  }
}

/** Device storage that belongs to the account, not to the device. */
const ACCOUNT_STORAGE_KEYS = [FILE_TOKEN_KEY, "busee:kanban:project", "busee:kanban-city:colors:v1"];

function forgetAccountStorage(): void {
  storedFileToken = null;
  try {
    for (const key of ACCOUNT_STORAGE_KEYS) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable: the in-memory copy is already gone.
  }
}

/** Resolve a stored file to a URL the browser can load. */
export function fileUrl(
  record: { id: string; collectionId: string; collectionName: string },
  filename: string,
  options?: { thumb?: string; download?: boolean },
): string {
  return pb.files.getURL(record, filename, { ...options, token: heldFileToken() });
}

/** Throws when the server cannot be reached; resolves otherwise. */
export async function checkServerReachable(): Promise<void> {
  await pb.health.check({ requestKey: null });
}

/** Sign in with an account's email and password. */
export async function signInWithPassword(
  email: string,
  password: string,
): Promise<UsersResponse> {
  const result = await usersCollection().authWithPassword(email.trim(), password, {
    requestKey: null,
  });
  return result.record;
}

export function signOut(): void {
  forgetAccountStorage();
  pb.authStore.clear();
}

/**
 * Exchange the stored token for a fresh one.
 *
 * Returns whether the session is still usable. A network failure deliberately
 * keeps the session: the token has not been rejected, we simply could not ask.
 * Only an actual 401/403 clears it — otherwise a flaky connection would sign
 * the user out of an app they cannot even reach to sign back into.
 */
export async function refreshSession(): Promise<boolean> {
  if (!pb.authStore.isValid) {
    // An expired session must not leave its email and file token on the device.
    signOut();
    return false;
  }

  try {
    await usersCollection().authRefresh({ requestKey: null });
    return true;
  } catch (error) {
    if (isAuthError(error)) {
      signOut();
      return false;
    }
    return true;
  }
}

export { Collections };
export type { CollectionResponses, UsersResponse };
